// Language translation hooks and utilities
import { useMemo, ReactNode } from "react";
import { useSelector } from "react-redux";
import {
  LanguageKey,
  isValidLanguageKey,
} from "../types/generated/LanguageKey";
import { RootState } from "../types/RootState";
import { store } from "../redux/store";

// Language state interface for Redux store
export interface LanguageState {
  [key: string]: string;
}

// Hook to get all language translations as a map
export const useLanguage = (): LanguageState => {
  const language = useSelector((state: RootState) => state.language || {});
  return language;
};

// Helper hook that uses the language state properly
export const useTranslate = () => {
  const language = useLanguage();

  return useMemo(() => {
    return (key: LanguageKey): string => {
      return language[key] || key;
    };
  }, [language]);
};

// Reactive translate hook that uses useSelector - works like language[key]
export const useTranslateFunction = () => {
  const language = useSelector((state: RootState) => state.language || {});

  return useMemo(() => {
    return (key: LanguageKey): string => {
      return language[key] || key;
    };
  }, [language]);
};

// Non-hook version that can be called anywhere (including inside callbacks)
// Gets language state from Redux store directly, similar to hasFeature
export const translate = (key: LanguageKey): string => {
  try {
    const state = store.getState();
    const languageState = state.language || {};
    return languageState[key] || key;
  } catch {
    return key;
  }
};

// API response validation
export const parseLanguageKeys = (apiKeys: unknown[]): LanguageKey[] => {
  if (!Array.isArray(apiKeys)) return [];

  return apiKeys
    .filter((key): key is string => typeof key === "string")
    .filter(isValidLanguageKey);
};

// Example component usage
export const TranslatedText = ({
  langKey,
  fallback,
}: {
  langKey: LanguageKey;
  fallback?: string;
}) => {
  const translate = useTranslate();

  // Your pattern: language["key"] => translate(LanguageKey.KEY)
  const text = translate(langKey);
  return <>{text === langKey && fallback ? fallback : text}</>;
};

// Higher-order component for conditional rendering based on language availability
export const LanguageGuard = ({
  langKey,
  children,
  fallback,
}: {
  langKey: LanguageKey;
  children: ReactNode;
  fallback?: ReactNode;
}) => {
  const language = useLanguage();
  const hasTranslation = Boolean(language[langKey]);

  return hasTranslation ? <>{children}</> : <>{fallback || null}</>;
};
